import { useContext, useMemo } from "react"
import PropTypes from "prop-types"
import { capitalize, startsWith } from "lodash"
import { DESC } from "helpers/sorts"
import { MetricsTemplateShape } from "components/custom-metrics-selector/helpers/prop-types"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useImpliedReturns } from "resources/implied-returns"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { FORECAST } from "helpers/shapes/simulator"
import { DEFAULT_HORIZON } from "helpers/forecasts/constants"
import { convertImpliedReturnsIdToUniversalId } from "helpers/securities"
import { useTemplateGatherers } from "../../../dashboard/hooks/use-template-gatherers"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"

export default function AssetViewCustomComponent(props) {
  const { templateMetadata, customSelectedMetrics, selectedMetrics, instrumentsWithMetadata, modelMetadata, highlightedRowIds, context, disableActiveTrading, instrumentColumns, contentSets, betaFields, betaInstrument, forecasts, classifications } = props
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    earliestDate,
    latestDate,
    modelId,
    performanceStartDate,
    isEndVsStartDateComparison,
    view,
  } = useContext(context)

  const hasImpliedReturns = useMemo(() => {
    return customSelectedMetrics.some((metric) => startsWith(metric.id, "impliedReturns"))
  }, [customSelectedMetrics])

  const [impliedReturns, impliedReturnsStatus] = useImpliedReturns({ modelId, positionSet: original, base: contextBase, from: earliestDate, to: latestDate, hasImpliedReturns })

  const forecastSecurities = useMemo(() => {
    if (impliedReturnsStatus !== "success" || !impliedReturns) {
      return null
    }
    const securities = [...impliedReturns.equities, ...impliedReturns.swaps, ...impliedReturns.indexes]
    return securities.map((security) => ({
      id: convertImpliedReturnsIdToUniversalId({ id: security.id }),
      expectedReturn: { return: security.expectedPercentReturn, horizon: DEFAULT_HORIZON },
    }))
  }, [impliedReturns, impliedReturnsStatus])

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })
  const gatherers = useTemplateGatherers(
    context,
    "custom",
    {
      selectedMetrics,
      contentSets,
      betaFields,
      betaInstrument,
      forecasts,
      computedPerformanceStartDate: performanceStartDate,
      forecastSecurities,
      classifications,
    },
  )

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "custom", items: customSelectedMetrics, isEndVsStartDateComparison }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, isEndVsStartDateComparison },
  )

  const filteredMetrics = useMemo(() => {
    return metrics.reduce((acc, metric) => {
      if (metric.dataPath) {
        acc.push(metric)
      }
      return acc
    }, [])
  }, [metrics])

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={filteredMetrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      downloadKey: view === "custom" ? "assets.custom" : `assets.custom.${view}`,
      downloadName: `Assets - ${templateMetadata?.name}`,
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      context,
    }}
  />
}

AssetViewCustomComponent.propTypes = {
  modelMetadata: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  templateMetadata: MetricsTemplateShape,
  customSelectedMetrics: PropTypes.arrayOf(PropTypes.object),
  selectedMetrics: PropTypes.arrayOf(PropTypes.string),
  instrumentsWithMetadata: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  betaFields: PropTypes.arrayOf(PropTypes.string),
  betaInstrument: PropTypes.object,
  forecasts: PropTypes.arrayOf(FORECAST),
}
